import React, { useState, useRef } from 'react';
import BrandLogo from '../components/BrandLogo';
import { UploadCloud, CheckCircle2, Mail, User, FileText, AlertCircle, Award, Star, Globe, Sparkles, Shield, Folder, File } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicPortal = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStats, setUploadStats] = useState({ total: 0, completed: 0, failed: 0 });
  
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const sendOTP = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    try {
      await axios.post(`${API}/send-otp`, { email });
      setOtpSent(true);
      toast.success('OTP sent to your email');
    } catch (error) {
      toast.error('Failed to send OTP');
    }
  };

  const verifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter valid 6-digit OTP');
      return;
    }

    try {
      await axios.post(`${API}/verify-otp`, { email, otp });
      setEmailVerified(true);
      toast.success('Email verified successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  // Get all files from a directory entry recursively
  const getFilesFromDirectory = async (dirEntry, path = '') => {
    const files = [];
    const dirReader = dirEntry.createReader();
    
    const readEntries = () => {
      return new Promise((resolve, reject) => {
        dirReader.readEntries((entries) => {
          resolve(entries);
        }, reject);
      });
    };

    let entries = await readEntries();
    while (entries.length > 0) {
      for (const entry of entries) {
        if (entry.isFile) {
          const file = await new Promise((resolve) => entry.file(resolve));
          // Store the relative path with the file
          file.relativePath = path ? `${path}/${entry.name}` : entry.name;
          files.push(file);
        } else if (entry.isDirectory) {
          const subPath = path ? `${path}/${entry.name}` : entry.name;
          const subFiles = await getFilesFromDirectory(entry, subPath);
          files.push(...subFiles);
        }
      }
      entries = await readEntries();
    }
    return files;
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (!emailVerified) {
      toast.error('Please verify your email first');
      return;
    }

    const items = e.dataTransfer.items;
    const allFiles = [];

    // Check if we have DataTransferItemList (for folder support)
    if (items) {
      const entries = [];
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) {
          entries.push(entry);
        }
      }

      for (const entry of entries) {
        if (entry.isDirectory) {
          toast.info(`Processing folder: ${entry.name}...`);
          const folderFiles = await getFilesFromDirectory(entry, entry.name);
          allFiles.push(...folderFiles);
        } else if (entry.isFile) {
          const file = await new Promise((resolve) => entry.file(resolve));
          file.relativePath = file.name;
          allFiles.push(file);
        }
      }
    } else {
      // Fallback for browsers without folder support
      const droppedFiles = Array.from(e.dataTransfer.files);
      droppedFiles.forEach(f => { f.relativePath = f.name; });
      allFiles.push(...droppedFiles);
    }

    if (allFiles.length > 0) {
      await uploadFiles(allFiles);
    }
  };

  const handleFileSelect = async (e) => {
    if (!emailVerified) {
      toast.error('Please verify your email first');
      return;
    }

    const selectedFiles = Array.from(e.target.files);
    // For regular file input, use webkitRelativePath if available (folder upload)
    selectedFiles.forEach(f => {
      f.relativePath = f.webkitRelativePath || f.name;
    });
    await uploadFiles(selectedFiles);
    
    // Reset the input so the same folder can be selected again
    e.target.value = '';
  };

  const uploadFiles = async (filesToUpload) => {
    const totalSize = [...files, ...filesToUpload].reduce((sum, f) => sum + (f.size || f.file_size), 0);
    if (totalSize > 1073741824) {
      toast.error('Total file size cannot exceed 1GB');
      return;
    }

    setUploading(true);
    setUploadStats({ total: filesToUpload.length, completed: 0, failed: 0 });
    
    const uploadedFiles = [];
    let completed = 0;
    let failed = 0;

    // Process files in batches of 3 for better performance
    const batchSize = 3;
    for (let i = 0; i < filesToUpload.length; i += batchSize) {
      const batch = filesToUpload.slice(i, i + batchSize);
      
      await Promise.all(batch.map(async (file) => {
        const displayName = file.relativePath || file.name;
        try {
          // Generate presigned URL with relative path for folder structure
          const { data } = await axios.post(`${API}/generate-upload-url`, {
            filename: file.name,
            file_size: file.size,
            content_type: file.type || 'application/octet-stream',
            relative_path: file.relativePath || null
          });

          // Upload to S3
          await axios.put(data.upload_url, file, {
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
            onUploadProgress: (progressEvent) => {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(prev => ({ ...prev, [displayName]: progress }));
            }
          });

          uploadedFiles.push({
            file_name: file.name,
            file_size: file.size,
            s3_key: data.s3_key,
            relative_path: file.relativePath || file.name
          });

          completed++;
          setUploadStats(prev => ({ ...prev, completed }));
        } catch (error) {
          console.error(`Failed to upload ${displayName}:`, error);
          failed++;
          setUploadStats(prev => ({ ...prev, failed }));
        }
      }));
    }

    // Add successfully uploaded files to state
    if (uploadedFiles.length > 0) {
      setFiles(prev => [...prev, ...uploadedFiles]);
      toast.success(`${uploadedFiles.length} file(s) uploaded successfully`);
    }
    
    if (failed > 0) {
      toast.error(`${failed} file(s) failed to upload`);
    }

    setUploading(false);
    setUploadProgress({});
    setUploadStats({ total: 0, completed: 0, failed: 0 });
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const submitJob = async () => {
    if (!name || !emailVerified || files.length === 0) {
      toast.error('Please fill all required fields and upload at least one file');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/submit-test-job`, {
        name,
        email,
        notes,
        files
      });

      setSubmitted(true);
      toast.success('Test job submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit job');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-8">
        <div className="max-w-2xl w-full text-center">
          <div className="bg-white rounded-xl border border-zinc-200 p-12 shadow-sm">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-zinc-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Submission Successful!
            </h1>
            <p className="text-lg text-zinc-600 mb-8">
              Thank you for submitting your test job. We&apos;ll review your files and get back to you soon.
            </p>
            <Button
              data-testid="submit-another-btn"
              onClick={() => {
                setSubmitted(false);
                setName('');
                setEmail('');
                setNotes('');
                setFiles([]);
                setEmailVerified(false);
                setOtpSent(false);
                setOtp('');
              }}
              className="h-11 px-8 rounded-md bg-zinc-900 text-white hover:bg-zinc-800"
            >
              Submit Another Job
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center p-8 lg:p-16 bg-white">
        <div className="max-w-xl mx-auto w-full">
          {/* Logo & Company Name */}
          <div className="mb-12">
            <BrandLogo className="h-12 mb-4" />
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-zinc-900 mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Submit Your Test Job
          </h1>

          <div className="space-y-6">
            {/* Name Field */}
            <div>
              <Label data-testid="name-label" className="text-sm font-medium mb-2 block">Name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  data-testid="name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  className="pl-10 h-11"
                />
              </div>
            </div>

            {/* Email Field */}
            <div>
              <Label data-testid="email-label" className="text-sm font-medium mb-2 block">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  disabled={emailVerified}
                  className="pl-10 h-11"
                />
              </div>
            </div>

            {/* Email Verification */}
            {!emailVerified && (
              <div className="space-y-3">
                {!otpSent ? (
                  <Button
                    data-testid="send-otp-btn"
                    onClick={sendOTP}
                    variant="outline"
                    className="w-full h-11"
                  >
                    Verify Email
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <Input
                      data-testid="otp-input"
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                      className="h-11 text-center text-lg tracking-widest"
                    />
                    <p className="text-sm text-zinc-500 text-center">
                      Don&apos;t see the email? Check your spam or junk folder.
                    </p>
                    <div className="flex gap-3">
                      <Button
                        data-testid="verify-otp-btn"
                        onClick={verifyOTP}
                        className="flex-1 h-11 bg-zinc-900 text-white hover:bg-zinc-800"
                      >
                        Verify OTP
                      </Button>
                      <Button
                        data-testid="resend-otp-btn"
                        onClick={sendOTP}
                        variant="outline"
                        className="h-11"
                      >
                        Resend
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {emailVerified && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Email verified successfully!
                </AlertDescription>
              </Alert>
            )}

            {/* Notes Field */}
            <div>
              <Label data-testid="notes-label" className="text-sm font-medium mb-2 block">Notes (Optional)</Label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 w-5 h-5 text-zinc-400" />
                <Textarea
                  data-testid="notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional information..."
                  className="pl-10 min-h-[100px]"
                />
              </div>
            </div>

            {/* File Upload Section */}
            <div>
              <Label data-testid="upload-label" className="text-sm font-medium mb-2 block">Upload Files *</Label>
              
              {/* Hidden file inputs */}
              <input
                ref={fileInputRef}
                data-testid="file-input"
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <input
                ref={folderInputRef}
                data-testid="folder-input"
                type="file"
                webkitdirectory=""
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {/* Upload Zone */}
              <div
                data-testid="upload-zone"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`relative flex flex-col items-center justify-center w-full h-48 rounded-xl border-2 border-dashed transition-all ${
                  isDragging
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-zinc-300 bg-zinc-50'
                } ${!emailVerified ? 'opacity-60' : ''}`}
              >
                <UploadCloud className="w-10 h-10 text-zinc-400 mb-3" />
                <p className="text-sm font-medium text-zinc-600 mb-1">
                  {uploading ? 'Uploading...' : 'Drag & drop files or folders here'}
                </p>
                <p className="text-xs text-zinc-500 mb-3">Maximum total size: 1GB</p>
                {!emailVerified && (
                  <p className="text-xs text-amber-600 font-medium">Please verify your email to enable upload</p>
                )}
              </div>
              
              {/* Upload Buttons */}
              {emailVerified && (
                <div className="flex gap-3 mt-3">
                  <Button
                    data-testid="select-files-btn"
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    className="flex-1 h-10"
                    disabled={uploading}
                  >
                    <File className="w-4 h-4 mr-2" />
                    Select Files
                  </Button>
                  <Button
                    data-testid="select-folder-btn"
                    onClick={() => folderInputRef.current?.click()}
                    variant="outline"
                    className="flex-1 h-10"
                    disabled={uploading}
                  >
                    <Folder className="w-4 h-4 mr-2" />
                    Select Folder
                  </Button>
                </div>
              )}

              {/* Upload Progress */}
              {uploading && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-blue-900">
                      Uploading {uploadStats.completed + uploadStats.failed} of {uploadStats.total} files
                    </span>
                    {uploadStats.failed > 0 && (
                      <span className="text-xs text-red-600">{uploadStats.failed} failed</span>
                    )}
                  </div>
                  
                  {/* Overall progress bar */}
                  <div className="w-full bg-blue-200 rounded-full h-2 mb-3">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadStats.total > 0 ? ((uploadStats.completed + uploadStats.failed) / uploadStats.total) * 100 : 0}%` }}
                    />
                  </div>
                  
                  {/* Individual file progress */}
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {Object.entries(uploadProgress).map(([fileName, progress]) => (
                      <div key={fileName} className="flex items-center gap-2 text-xs">
                        <div className="flex-1 truncate text-zinc-600">{fileName}</div>
                        <div className="w-16 bg-zinc-200 rounded-full h-1.5">
                          <div 
                            className="bg-blue-600 h-1.5 rounded-full"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="w-8 text-right text-zinc-500">{progress}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Uploaded Files List */}
              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-zinc-700">{files.length} file(s) ready</span>
                    <span className="text-xs text-zinc-500">
                      {(files.reduce((sum, f) => sum + f.file_size, 0) / 1024 / 1024).toFixed(2)} MB / 1024 MB
                    </span>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {files.map((file, index) => (
                      <div
                        key={index}
                        data-testid={`uploaded-file-${index}`}
                        className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg border border-zinc-200"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-zinc-900 truncate" title={file.relative_path || file.file_name}>
                              {file.relative_path || file.file_name}
                            </p>
                            <p className="text-xs text-zinc-500">
                              {(file.file_size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <Button
                          data-testid={`remove-file-${index}`}
                          onClick={() => removeFile(index)}
                          variant="ghost"
                          size="sm"
                          className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <Button
              data-testid="submit-job-btn"
              onClick={submitJob}
              disabled={!name || !emailVerified || files.length === 0 || submitting}
              className="w-full h-11 bg-zinc-900 text-white hover:bg-zinc-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit Test Job'}
            </Button>
          </div>

          {/* Footer */}
          <div className="mt-12 p-6 bg-zinc-50 rounded-lg border border-zinc-200">
            <p className="text-sm text-zinc-700 font-medium mb-2">Having trouble uploading?</p>
            <p className="text-sm text-zinc-600 mb-3">
              If you&apos;re experiencing issues with the upload form, you can use our Dropbox upload link or contact us directly.
            </p>
            <a
              href="https://www.dropbox.com/request/k9Uo35DOvSM6SJ97I2hU"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-700 underline"
            >
              Dropbox link: https://www.dropbox.com/request/k9Uo35DOvSM6SJ97I2hU
            </a>
          </div>
        </div>
      </div>

      {/* Right Panel - Trust & Highlights */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-zinc-50 to-zinc-100 relative">
        <div className="sticky top-0 h-screen overflow-y-auto p-8 lg:p-12 flex flex-col justify-center">
          <div className="max-w-lg mx-auto space-y-8">
            
            {/* Section 1: Why RetouchingPix */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-zinc-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Award className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Why RetouchingPix?
                </h3>
              </div>
              
              <p className="text-zinc-600 mb-6 leading-relaxed">
                At RetouchingPix, we specialize in delivering high-end, consistent, and marketplace-ready image retouching for global brands and agencies.
              </p>
              
              <div className="space-y-3 mb-6">
                <p className="text-sm font-semibold text-zinc-700 mb-3">Our expertise covers:</p>
                <div className="flex items-start gap-3">
                  <Star className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600">Product Image Editing (eCommerce & Marketplace Ready)</span>
                </div>
                <div className="flex items-start gap-3">
                  <Star className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600">Fashion / On-Model Image Editing</span>
                </div>
                <div className="flex items-start gap-3">
                  <Sparkles className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600 font-medium">Editorial Image Retouching (Our most specialized service)</span>
                </div>
                <div className="flex items-start gap-3">
                  <Star className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600">Advanced Color Corrections</span>
                </div>
                <div className="flex items-start gap-3">
                  <Star className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-zinc-600">Real Estate Image Enhancement</span>
                </div>
              </div>
              
              <div className="flex items-center gap-4 pt-4 border-t border-zinc-100">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-zinc-400" />
                  <span className="text-xs text-zinc-500">USA, France, UK, Italy</span>
                </div>
                <div className="text-xs text-zinc-400">•</div>
                <span className="text-xs text-zinc-500 font-semibold">15+ Years Experience</span>
                <div className="text-xs text-zinc-400">•</div>
                <span className="text-xs text-zinc-500">20+ Global Clients</span>
              </div>
              
              <p className="text-sm text-zinc-600 mt-4 italic">
                Our focus is not just editing images — but creating visuals that sell, inspire, and elevate brand value.
              </p>
            </div>
            
            {/* Section 2: Free Test Job */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-100 rounded-full -translate-y-16 translate-x-16 opacity-50" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-teal-100 rounded-full translate-y-12 -translate-x-12 opacity-50" />
              
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Free Test Job
                    </h3>
                    <p className="text-xs text-emerald-600 font-semibold">No Cost, No Commitment</p>
                  </div>
                </div>
                
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 mb-4">
                  <p className="text-zinc-700 font-medium">
                    Your test job is <span className="text-emerald-600 font-bold">100% free</span>.
                  </p>
                  <p className="text-sm text-zinc-600 mt-2">
                    This allows you to experience our quality, consistency, and attention to detail before moving forward.
                  </p>
                </div>
                
                <p className="text-sm text-zinc-600 leading-relaxed">
                  We believe the best way to earn your trust is to let our work speak for itself — and show you why RetouchingPix stands apart from others.
                </p>
              </div>
            </div>
            
          </div>
        </div>
      </div>

      {/* Mobile Trust Section - Shows below form on mobile */}
      <div className="lg:hidden bg-zinc-50 p-6 border-t border-zinc-200">
        <div className="space-y-6 max-w-xl mx-auto">
          
          {/* Mobile: Why RetouchingPix */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-zinc-200">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-bold text-zinc-900">Why RetouchingPix?</h3>
            </div>
            <p className="text-sm text-zinc-600 mb-4">
              We specialize in high-end, marketplace-ready image retouching for global brands and agencies.
            </p>
            <div className="space-y-2 text-sm text-zinc-600">
              <div className="flex items-center gap-2">
                <Star className="w-3 h-3 text-blue-500" />
                <span>Product & eCommerce Editing</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-3 h-3 text-blue-500" />
                <span>Fashion & Editorial Retouching</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-3 h-3 text-blue-500" />
                <span>15+ Years Experience • 20+ Global Clients</span>
              </div>
            </div>
          </div>
          
          {/* Mobile: Free Test Job */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-emerald-600" />
              <h3 className="text-lg font-bold text-zinc-900">Free Test Job</h3>
              <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">No Cost</span>
            </div>
            <p className="text-sm text-zinc-600">
              Your test job is <span className="font-semibold text-emerald-600">100% free</span>. Experience our quality before moving forward.
            </p>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default PublicPortal;